import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { ColumnType } from "antd/es/table";
import { useIntl } from "react-intl";

import FieldCategory from "@mapmycustomers/shared/enum/fieldModel/FieldCategory";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import { EntityTypesSupportedByIntegrations } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import IFieldModel, {
  CategorizedFields,
} from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import Table from "@app/component/Table";
import {
  changeIntegrationField,
  changeIntegrationFieldGroup,
} from "@app/store/integrations/actions";
import { getCurrentIntegration } from "@app/store/integrations/selectors";
import { RootState } from "@app/store/rootReducer";
import Integration from "@app/types/integrations/Integration";
import IntegrationField from "@app/types/integrations/IntegrationField";

import { getIntegrationAnalyticsSetupIssuer } from "../../integrationAnalytics";

import styles from "./FieldMappingTable.module.scss";
import useMappingTableColumnConfig from "./useMappingTableColumnConfig";

interface Props {
  changeFieldGrouping: typeof changeIntegrationFieldGroup;
  changeFieldMapping: typeof changeIntegrationField;
  currentIntegration?: Integration;
  dataset: IntegrationField[];
  entityType: EntityTypesSupportedByIntegrations;
  fieldModel: IFieldModel;
  onShowGroupFieldInfoModal: () => void;
  onShowMmcFieldInfoModal: () => void;
  serviceName: string;
}

const FieldMappingTable: React.FC<Props> = ({
  changeFieldGrouping,
  changeFieldMapping,
  currentIntegration,
  dataset,
  entityType,
  fieldModel,
  onShowGroupFieldInfoModal,
  onShowMmcFieldInfoModal,
  serviceName,
}) => {
  const intl = useIntl();

  const [filterString, setFilterString] = useState("");
  const fieldCategories: CategorizedFields = useMemo(() => {
    const filter = filterString.toLowerCase().trim();
    const categorizedFields = fieldModel.getIntegrationCategorizedFields(currentIntegration?.type);
    Object.keys(categorizedFields).forEach((category) => {
      categorizedFields[category as FieldCategory] = categorizedFields[
        category as FieldCategory
      ].filter(
        (field: IField) =>
          !field.hasFeature(FieldFeature.NON_INTEGRATION) &&
          field.displayName.toLowerCase().includes(filter)
      );
    });
    return categorizedFields;
  }, [currentIntegration, fieldModel, filterString]);

  const usedFieldNames = useMemo(
    () => new Set(dataset.map(({ mmcField }) => mmcField || undefined).filter(isDefined)),
    [dataset]
  );

  const handleChangeFieldMapping = useCallback(
    (mapping) => {
      changeFieldMapping(mapping);
      getIntegrationAnalyticsSetupIssuer(currentIntegration?.type, 2).clicked(
        ["Change Field Selected"],
        {
          mapping,
        }
      );
    },
    [changeFieldMapping, currentIntegration]
  );

  const handleChangeFieldGrouping = useCallback(
    (grouping) => {
      changeFieldGrouping(grouping);
      getIntegrationAnalyticsSetupIssuer(currentIntegration?.type, 2).clicked(
        [`Toggle Create Group ${grouping.enabled ? "On" : "Off"}`],
        {
          grouping,
        }
      );
    },
    [changeFieldGrouping, currentIntegration]
  );

  const tableColumnConfig: ColumnType<IntegrationField>[] = useMappingTableColumnConfig(
    intl,
    entityType,
    fieldModel,
    fieldCategories,
    serviceName,
    usedFieldNames,
    setFilterString,
    onShowMmcFieldInfoModal,
    onShowGroupFieldInfoModal,
    handleChangeFieldMapping,
    handleChangeFieldGrouping,
    currentIntegration
  );

  return (
    <Table<IntegrationField>
      className={styles.table}
      columns={tableColumnConfig}
      dataSource={dataset}
      noHover
      pagination={false}
      rowKey="id"
      scroll={{ y: 500 }} // max height - 500
    />
  );
};
const mapStateToProps = (state: RootState) => ({
  currentIntegration: getCurrentIntegration(state),
});
const mapDispatchToProps = {
  changeFieldGrouping: changeIntegrationFieldGroup,
  changeFieldMapping: changeIntegrationField,
};

export default connect(mapStateToProps, mapDispatchToProps)(FieldMappingTable);
